import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, listStages, type StageDetail } from '../api.js';

/**
 * The project's eight stages and their gate items, fetched ONCE for the page.
 *
 * Two things read them now and they must agree: the lifecycle strip, and the
 * "Needs you now" panel that turns the active stages' unsatisfied gate items
 * into a list of links. A panel saying a stage wants a budget while the strip
 * below it shows the item ticked is worse than either alone, so there is one
 * fetch and one copy of the answer — and a stage move REPLACES it from the
 * server's response rather than re-reading (`moveStage` returns the new list).
 *
 * The fallback is the same as `ProjectMembers`: a section rendered on its own,
 * which is how every screen test renders it, fetches for itself.
 */

export type StagesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly stages: readonly StageDetail[] }
  /** `04` gives a Client/Consultant no `stage` row at all. Not a failure. */
  | { readonly status: 'not_permitted' }
  | { readonly status: 'failed'; readonly message: string };

interface StagesContextValue {
  readonly projectId: string;
  readonly state: StagesState;
  readonly reload: () => Promise<void>;
  readonly replace: (stages: readonly StageDetail[]) => void;
}

const StagesContext = createContext<StagesContextValue | null>(null);

async function fetchState(projectId: string): Promise<StagesState> {
  try {
    return { status: 'ready', stages: await listStages(projectId) };
  } catch (caught) {
    // 404 counts as refused, like everywhere else: a non-member gets 404 so the
    // project's existence is not confirmed (ADR-004).
    const denied = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
    return denied
      ? { status: 'not_permitted' }
      : { status: 'failed', message: 'Could not load the lifecycle.' };
  }
}

export function ProjectStagesProvider({
  projectId,
  active = true,
  children,
}: {
  readonly projectId: string;
  /**
   * Whether the screen that reads this is on show.
   *
   * ONLY THE OVERVIEW READS THE STAGES — `Lifecycle` and `NeedsYouNow`, and
   * nothing else — and the gate items they draw are computed from records that
   * live on every OTHER tab. That combination is what made this stale.
   *
   * The provider wraps the whole project page, so it survives a tab change
   * while the Overview's own components unmount and remount underneath it. It
   * fetched once, on mount, and never again. So: set a budget on Commercial,
   * come back to the Overview, and the Award gate still says "Baseline budget
   * above zero ✗" — with the three budget lines sitting one tab away and the
   * server, asked directly, answering that the item is satisfied. Reported on
   * the first run through the product, and it would have read as the gate being
   * broken rather than the screen being old.
   *
   * Refetching when the reader ARRIVES is the right boundary rather than making
   * ten registers each remember to invalidate this after a create — that is a
   * second copy of "what feeds a gate", and the first register to forget it
   * puts the bug straight back.
   */
  readonly active?: boolean;
  readonly children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<StagesState>({ status: 'loading' });

  const reload = useCallback(async () => {
    setState(await fetchState(projectId));
  }, [projectId]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    void (async () => {
      const next = await fetchState(projectId);
      if (!cancelled) setState(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, active]);

  const replace = useCallback((stages: readonly StageDetail[]) => {
    setState({ status: 'ready', stages });
  }, []);

  return (
    <StagesContext.Provider value={{ projectId, state, reload, replace }}>
      {children}
    </StagesContext.Provider>
  );
}

/** The stages, falling back to an own fetch when no provider covers them. */
export function useProjectStagesState(projectId: string): StagesContextValue {
  const provided = useContext(StagesContext);
  const covered = provided !== null && provided.projectId === projectId;

  const [own, setOwn] = useState<StagesState>({ status: 'loading' });

  const reloadOwn = useCallback(async () => {
    setOwn(await fetchState(projectId));
  }, [projectId]);

  useEffect(() => {
    if (covered) return undefined;
    let cancelled = false;
    void (async () => {
      const next = await fetchState(projectId);
      if (!cancelled) setOwn(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [covered, projectId]);

  const replaceOwn = useCallback((stages: readonly StageDetail[]) => {
    setOwn({ status: 'ready', stages });
  }, []);

  if (covered) return provided;
  return { projectId, state: own, reload: reloadOwn, replace: replaceOwn };
}
